import { CircleDollarSign, Hash } from 'lucide-react';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, XAxis, YAxis } from 'recharts';

import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/shadcn/chart';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/shadcn/table';
import { useChartHeightClass } from '@/hooks/use-chart-height';
import { useTranslations } from '@/hooks/useTranslations';
import { cn, formatCurrency } from '@/lib/utils';
import type { CategoricalResult, ComparisonResult, TemporalResult, ValueFormat, WidgetResult } from '@/types/dashboards';

interface WidgetChartProps {
    result: WidgetResult;
}

function formatValue(value: number, format: ValueFormat): string {
    return format === 'currency' ? formatCurrency(value) : new Intl.NumberFormat('de-DE').format(value);
}

/**
 * Renders a normalized ReportQueryService payload "blindly" — the backend has
 * already resolved labels, colors, and shape, so this only maps chart_type to
 * the matching recharts primitive. KPI renders as a large number.
 */
export default function WidgetChart({ result }: WidgetChartProps) {
    const { t } = useTranslations();
    const heightClass = useChartHeightClass('md');

    if (result.kind === 'error') {
        return (
            <div className={cn('text-muted-foreground flex items-center justify-center text-sm', heightClass)}>
                {t('dashboards.builder.widget_error', 'This widget could not be loaded.')}
            </div>
        );
    }

    if (result.kind === 'kpi') {
        const Icon = result.format === 'currency' ? CircleDollarSign : Hash;
        return (
            <div className="flex items-center gap-4 py-1">
                <div className="bg-primary/10 text-primary flex h-11 w-11 shrink-0 items-center justify-center rounded-xl">
                    <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                    <div className="text-2xl font-bold tracking-tight sm:text-3xl">{formatValue(result.value, result.format)}</div>
                    <div className="text-muted-foreground truncate text-xs">{result.measure_label}</div>
                </div>
            </div>
        );
    }

    if (result.chart_type === 'table') {
        return (
            <WidgetTable result={result} heightClass={heightClass} emptyLabel={t('dashboards.builder.no_data', 'No data for the selected period.')} />
        );
    }

    const isEmpty = result.data.length === 0;
    if (isEmpty) {
        return (
            <div className={cn('text-muted-foreground flex items-center justify-center text-sm', heightClass)}>
                {t('dashboards.builder.no_data', 'No data for the selected period.')}
            </div>
        );
    }

    const tooltip = <ChartTooltip content={<ChartTooltipContent />} />;

    if (result.kind === 'comparison') {
        const ComparisonChart = result.chart_type === 'area' ? AreaChart : result.chart_type === 'bar' ? BarChart : LineChart;
        return (
            <ChartContainer config={result.chartConfig} className={cn('w-full', heightClass)}>
                <ComparisonChart data={result.data}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted opacity-30" />
                    <XAxis dataKey="x" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    {tooltip}
                    <Legend iconType="circle" />
                    {result.series.map((serie) => {
                        if (result.chart_type === 'bar') {
                            return <Bar key={serie.key} dataKey={serie.key} name={serie.name} fill={serie.color} radius={[4, 4, 0, 0]} />;
                        }
                        if (result.chart_type === 'area') {
                            return (
                                <Area
                                    key={serie.key}
                                    dataKey={serie.key}
                                    name={serie.name}
                                    stroke={serie.color}
                                    fill={serie.color}
                                    fillOpacity={0.15}
                                    strokeWidth={2}
                                />
                            );
                        }
                        return <Line key={serie.key} dataKey={serie.key} name={serie.name} stroke={serie.color} strokeWidth={2} dot={{ r: 2 }} />;
                    })}
                </ComparisonChart>
            </ChartContainer>
        );
    }

    if (result.kind === 'categorical') {
        // Pie/donut renders slices; every other chart type plots value over the
        // categorical axis.
        if (result.chart_type === 'pie') {
            return (
                <ChartContainer config={result.chartConfig} className={cn('w-full', heightClass)}>
                    <PieChart>
                        <ChartTooltip content={<ChartTooltipContent nameKey="label" />} />
                        <Legend layout="vertical" align="right" verticalAlign="middle" iconType="circle" />
                        <Pie data={result.data} dataKey="value" nameKey="label" innerRadius={55} outerRadius={100} paddingAngle={2}>
                            {result.data.map((entry, i) => (
                                <Cell key={i} fill={entry.fill} />
                            ))}
                        </Pie>
                    </PieChart>
                </ChartContainer>
            );
        }

        if (result.chart_type === 'line' || result.chart_type === 'area') {
            const Chart = result.chart_type === 'area' ? AreaChart : LineChart;
            return (
                <ChartContainer config={result.chartConfig} className={cn('w-full', heightClass)}>
                    <Chart data={result.data}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted opacity-30" />
                        <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        {tooltip}
                        {result.chart_type === 'area' ? (
                            <Area dataKey="value" stroke="var(--chart-1)" fill="var(--chart-1)" fillOpacity={0.2} strokeWidth={2} />
                        ) : (
                            <Line dataKey="value" stroke="var(--chart-1)" strokeWidth={2} dot={{ r: 3 }} />
                        )}
                    </Chart>
                </ChartContainer>
            );
        }

        return (
            <ChartContainer config={result.chartConfig} className={cn('w-full', heightClass)}>
                <BarChart data={result.data}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted opacity-30" />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    {tooltip}
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {result.data.map((entry, i) => (
                            <Cell key={i} fill={entry.fill} />
                        ))}
                    </Bar>
                </BarChart>
            </ChartContainer>
        );
    }

    // temporal — a continuous, gap-filled period series
    const color = result.series[0]?.color ?? 'var(--chart-1)';

    if (result.chart_type === 'bar') {
        return (
            <ChartContainer config={result.chartConfig} className={cn('w-full', heightClass)}>
                <BarChart data={result.data}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted opacity-30" />
                    <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    {tooltip}
                    <Bar dataKey="value" fill={color} radius={[4, 4, 0, 0]} />
                </BarChart>
            </ChartContainer>
        );
    }

    const Chart = result.chart_type === 'area' ? AreaChart : LineChart;
    return (
        <ChartContainer config={result.chartConfig} className={cn('w-full', heightClass)}>
            <Chart data={result.data}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted opacity-30" />
                <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 11 }} />
                {tooltip}
                {result.chart_type === 'area' ? (
                    <Area dataKey="value" stroke={color} fill={color} fillOpacity={0.2} strokeWidth={2} />
                ) : (
                    <Line dataKey="value" stroke={color} strokeWidth={2} dot={{ r: 3 }} />
                )}
            </Chart>
        </ChartContainer>
    );
}

/** Scannable table rendering — the "check deeper" view for categorical, temporal, and comparison results. */
function WidgetTable({
    result,
    heightClass,
    emptyLabel,
}: {
    result: CategoricalResult | TemporalResult | ComparisonResult;
    heightClass: string;
    emptyLabel: string;
}) {
    if (result.data.length === 0) {
        return <div className={cn('text-muted-foreground flex items-center justify-center text-sm', heightClass)}>{emptyLabel}</div>;
    }

    let columns: string[];
    let rows: Array<{ key: string; cells: string[] }>;

    if (result.kind === 'comparison') {
        columns = [result.x_label, ...result.series.map((serie) => serie.name)];
        rows = result.data.map((row, index) => ({
            key: `${String(row.x ?? '')}-${index}`,
            cells: [String(row.x ?? ''), ...result.series.map((serie) => formatValue(Number(row[serie.key] ?? 0), serie.format))],
        }));
    } else if (result.kind === 'temporal') {
        columns = [result.x_label, result.measure_label];
        rows = result.data.map((datum, index) => ({
            key: `${datum.period}-${index}`,
            cells: [datum.period, formatValue(datum.value, result.format)],
        }));
    } else {
        columns = [result.x_label, result.measure_label];
        rows = result.data.map((datum, index) => ({ key: `${datum.label}-${index}`, cells: [datum.label, formatValue(datum.value, result.format)] }));
    }

    return (
        <div className={cn('overflow-auto', heightClass)}>
            <Table>
                <TableHeader>
                    <TableRow>
                        {columns.map((column, index) => (
                            <TableHead key={index} className={index === 0 ? '' : 'text-right'}>
                                {column}
                            </TableHead>
                        ))}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow key={row.key}>
                            {row.cells.map((cell, index) => (
                                <TableCell key={index} className={index === 0 ? 'font-medium' : 'text-right tabular-nums'}>
                                    {cell}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}
